/**
 * Baby Names — reveal page
 *
 * Polls the public session API every 5s until status = 'revealed'.
 * On reveal: shows the winning name (highest score) with a falling-leaves animation.
 */
(function () {
  const API_BASE = '/apps/registry/api';
  const POLL_INTERVAL = 5000;
  const LEAVES = ['🍃', '🌿', '🍀', '🌱', '🍂', '🌾'];

  let SHARE_CODE = null;
  let pollTimer = null;
  let revealed = false;

  function esc(str) {
    if (!str) return '';
    return String(str).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  }

  async function fetchSession() {
    const r = await fetch(`${API_BASE}/naming/public/${SHARE_CODE}`);
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
    return data.session;
  }

  // ── Falling leaves animation ──────────────────────────────────────────────

  function launchLeaves() {
    const count = 40;
    for (let i = 0; i < count; i++) {
      setTimeout(() => spawnLeaf(), i * 120);
    }
  }

  function spawnLeaf() {
    const leaf = document.createElement('span');
    leaf.className = 'bn-leaf';
    leaf.textContent = LEAVES[Math.floor(Math.random() * LEAVES.length)];
    const left = Math.random() * 100;
    const duration = 3 + Math.random() * 4;
    const drift = (Math.random() - 0.5) * 200;
    leaf.style.cssText = `
      left: ${left}vw;
      font-size: ${1 + Math.random() * 1.2}rem;
      animation-duration: ${duration}s;
      --drift: ${drift}px;
    `;
    document.body.append(leaf);
    leaf.addEventListener('animationend', () => leaf.remove());
  }

  // ── Show reveal ───────────────────────────────────────────────────────────

  function showReveal(session) {
    const page = document.getElementById('bn-reveal-page');
    if (!page) return;

    // Find top-scored name
    const names = (session.names || []).sort((a, b) => {
      const sa = a.votes ? a.votes.score : 0;
      const sb = b.votes ? b.votes.score : 0;
      return sb - sa;
    });

    const winner = names[0];
    if (!winner) {
      page.innerHTML = `
        <div class="bn-reveal-page__waiting">
          <h1 style="color:var(--bn-sage)">Coming soon…</h1>
          <p>The reveal is on its way!</p>
        </div>
      `;
      return;
    }

    page.innerHTML = `
      <div class="bn-reveal-name" id="bn-reveal-name-box">
        <div class="bn-reveal-name__label">The name is…</div>
        <div class="bn-reveal-name__text" id="bn-reveal-text">${esc(winner.name)}</div>
        <div class="bn-reveal-name__from" id="bn-reveal-from">
          ${winner.gender ? `<span class="bn-gender bn-gender--${winner.gender}" style="margin-right:8px">${winner.gender}</span>` : ''}
          ${winner.origin ? `Origin: ${esc(winner.origin)}` : ''}
          ${winner.votes ? ` · Loved by ${winner.votes.love} ${winner.votes.love === 1 ? 'person' : 'people'}` : ''}
        </div>
      </div>
    `;

    launchLeaves();

    // Staggered reveal animation
    requestAnimationFrame(() => {
      setTimeout(() => {
        document.getElementById('bn-reveal-text')?.classList.add('bn-reveal-name__text--show');
        setTimeout(() => {
          document.getElementById('bn-reveal-from')?.classList.add('bn-reveal-name__from--show');
        }, 600);
      }, 300);
    });
  }

  // ── Waiting state ─────────────────────────────────────────────────────────

  function showWaiting(session) {
    const page = document.getElementById('bn-reveal-page');
    if (!page) return;
    page.innerHTML = `
      <div class="bn-reveal-page__waiting">
        <div class="bn-spinner" style="width:32px;height:32px;border-width:3px"></div>
        <h1>${esc(session.title)}</h1>
        <p>The big reveal hasn't happened yet. Come back soon — this page will update automatically!</p>
        <div style="font-size:0.85rem;color:var(--bn-muted);margin-top:8px">${session.contributorCount} friend${session.contributorCount !== 1 ? 's' : ''} have voted</div>
      </div>
    `;
  }

  // ── Poll ──────────────────────────────────────────────────────────────────

  async function poll() {
    if (revealed) return;
    try {
      const session = await fetchSession();
      if (session.status === 'revealed') {
        revealed = true;
        clearInterval(pollTimer);
        showReveal(session);
      }
    } catch (e) {
      console.warn('Reveal poll error:', e.message);
    }
  }

  // ── Boot ──────────────────────────────────────────────────────────────────

  async function boot() {
    SHARE_CODE = new URLSearchParams(location.search).get('code');

    const page = document.getElementById('bn-reveal-page');
    if (!SHARE_CODE) {
      if (page) page.innerHTML = '<div class="bn-empty"><p>No code provided.</p></div>';
      return;
    }

    try {
      const session = await fetchSession();

      if (session.status === 'revealed') {
        revealed = true;
        showReveal(session);
      } else if (session.status === 'closed') {
        if (page) page.innerHTML = `
          <div class="bn-reveal-page__waiting">
            <h1>${esc(session.title)}</h1>
            <p>Voting has closed. The reveal is coming soon!</p>
          </div>
        `;
        pollTimer = setInterval(poll, POLL_INTERVAL);
      } else {
        showWaiting(session);
        pollTimer = setInterval(poll, POLL_INTERVAL);
      }
    } catch (e) {
      if (page) page.innerHTML = '<div class="bn-empty"><p>Could not load reveal page. Check your link.</p></div>';
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
